import * as i0 from '@angular/core';
import { Injectable, Component, ChangeDetectionStrategy, Injector, NgModule } from '@angular/core';
import * as i6 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { Subject, interval, throwError } from 'rxjs';
import { takeUntil, tap, catchError, finalize } from 'rxjs/operators';
import { remove } from 'lodash-es';
import * as i3 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i4 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import * as i5 from '@angular/material/progress-spinner';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import * as i7 from '@angular/material/tooltip';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as i8 from '@firestitch/date';
import { FsDateModule } from '@firestitch/date';
import * as i2 from '@angular/cdk/overlay';
import { HttpContextToken, HttpEventType, HTTP_INTERCEPTORS } from '@angular/common/http';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

var UploadFileStatus;
(function (UploadFileStatus) {
    UploadFileStatus["Uploading"] = "uploading";
    UploadFileStatus["Processing"] = "processing";
    UploadFileStatus["Uploaded"] = "uploaded";
    UploadFileStatus["Failed"] = "failed";
    UploadFileStatus["Cancelled"] = "cancelled";
    UploadFileStatus["Queued"] = "queued";
})(UploadFileStatus || (UploadFileStatus = {}));

class UploadService {
    constructor() {
        this._files = [];
        this._filesAdded$ = new Subject();
    }
    get filesAdded$() {
        return this._filesAdded$;
    }
    get files() {
        return this._files;
    }
    addFiles(files) {
        this._filesAdded$.next(files);
        this._files.push(...files);
    }
    removeFiles(files) {
        remove(this._files, (file) => {
            return files.indexOf(file) >= 0;
        });
    }
}
UploadService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UploadService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
UploadService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UploadService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UploadService, decorators: [{
            type: Injectable
        }] });

class FsUploadComponent {
    constructor(_dialogRef, _uploadService, _cdRef) {
        this._dialogRef = _dialogRef;
        this._uploadService = _uploadService;
        this._cdRef = _cdRef;
        this.files = [];
        this.failed = 0;
        this.uploaded = 0;
        this.processing = 0;
        this.cancelled = 0;
        this.uploading = 0;
        this.queued = 0;
        this.closingPercent = 0;
        this.closingSeconds = null;
        this.uploadTotalBytes = 0;
        this.uploadLoadedBytes = 0;
        this.UploadFileStatus = UploadFileStatus;
        this.bytesPerSecond = [];
        this._destroy$ = new Subject();
        this._addFiles(this._uploadService.files);
        this._uploadService.filesAdded$
            .pipe(takeUntil(this._destroy$))
            .subscribe((files) => {
            this._addFiles(files);
        });
        interval(1000)
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            if (this.uploading) {
                this._calcRemaning();
                this._cdRef.markForCheck();
            }
            if (!this.running) {
                this.closingSeconds--;
                this._cdRef.markForCheck();
                if (this.closingSeconds <= 0) {
                    this._clearClosing();
                    this._dialogRef.close();
                }
            }
        });
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    cancel(file) {
        file.cancel();
        this._update();
        this._cdRef.markForCheck();
    }
    get running() {
        return this.uploading || this.processing || this.queued;
    }
    _addFiles(files) {
        this.files.push(...files);
        this.uploadTotalBytes += files
            .reduce((total, file) => {
            return total + file.file.size;
        }, 0);
        this._clearClosing();
        files.forEach((file) => {
            this._processFile(file);
        });
        this._cdRef.markForCheck();
    }
    _calcRemaning() {
        const bytesPerSecond = this.bytesPerSecond.reduce((total, value) => {
            return total + value;
        }, 0);
        const avgBytesPerSecond = bytesPerSecond / this.bytesPerSecond.length;
        const bytes = this.uploadTotalBytes - this.uploadLoadedBytes;
        this.remainingSeconds = Math.floor(bytes / avgBytesPerSecond);
    }
    _processFile(file) {
        file.statusSubject
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            this._update();
            this._cdRef.markForCheck();
        });
    }
    _update() {
        let uploading = 0;
        let uploaded = 0;
        let processing = 0;
        let failed = 0;
        let cancelled = 0;
        let queued = 0;
        let uploadLoadedBytes = 0;
        this.closingSeconds = null;
        this.files
            .forEach((file) => {
            uploadLoadedBytes += file.loaded;
            switch (file.status) {
                case UploadFileStatus.Uploading:
                    if (file.bytesPerSecond) {
                        this.bytesPerSecond.unshift(file.bytesPerSecond);
                    }
                    uploading++;
                    break;
                case UploadFileStatus.Processing:
                    processing++;
                    break;
                case UploadFileStatus.Uploaded:
                    uploaded++;
                    break;
                case UploadFileStatus.Cancelled:
                    cancelled++;
                    break;
                case UploadFileStatus.Failed:
                    failed++;
                    break;
                case UploadFileStatus.Queued:
                    queued++;
                    break;
            }
        });
        this.uploading = uploading;
        this.uploaded = uploaded;
        this.processing = processing;
        this.failed = failed;
        this.cancelled = cancelled;
        this.queued = queued;
        this.uploadLoadedBytes = uploadLoadedBytes;
        this.bytesPerSecond = this.bytesPerSecond.splice(0, 50);
        if (!this.running) {
            this._startClosing();
            this.remainingSeconds = 0;
        }
    }
    _clearClosing() {
        this.closingSeconds = null;
    }
    _startClosing() {
        const lastFile = this.files[this.files.length - 1];
        this.closingSeconds = (lastFile === null || lastFile === void 0 ? void 0 : lastFile.status) === UploadFileStatus.Failed ?
            20 : 0;
    }
}
FsUploadComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsUploadComponent, deps: [{ token: i1.MatDialogRef }, { token: UploadService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsUploadComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsUploadComponent, selector: "fs-component", ngImport: i0, template: "<div class=\"container\">\n  <div class=\"status-heading\">\n    <div class=\"title\">\n      <h2 mat-dialog-title>\n        Upload Status\n      </h2>\n      <div class=\"status\">\n        <span class=\"counts\">\n          <ng-container *ngIf=\"uploading || processing || queued; else elseUploadingProcessing\">\n            <span *ngIf=\"uploading + queued\">{{uploading + queued}} Uploading</span>\n            <span *ngIf=\"processing\">{{processing}} Processing</span>\n          </ng-container>\n          <ng-template #elseUploadingProcessing>\n            <span *ngIf=\"uploaded\">{{uploaded}} Successfully Uploaded</span>\n            <span *ngIf=\"failed\">{{failed}} Failed</span>\n            <span *ngIf=\"cancelled\">{{cancelled}} Cancelled</span>\n          </ng-template>\n        </span>\n        <span class=\"remaining\" *ngIf=\"remainingSeconds\">, {{remainingSeconds | fsDateDuration: { minutes: true, seconds: true} }} remaining</span>\n      </div>\n    </div>\n    <div class=\"close\">\n      <span *ngIf=\"closingSeconds > 0\" class=\"close-seconds\">Closing in {{closingSeconds}}</span>\n      <button mat-icon-button class=\"clear\" [mat-dialog-close]=\"true\" type=\"button\" tabindex=\"-1\">\n        <mat-icon>clear</mat-icon>\n      </button>\n    </div>\n  </div>\n</div>\n<mat-dialog-content>\n  <div class=\"files\">\n    <div *ngFor=\"let file of files\" class=\"file\" [ngClass]=\"file.status\">\n      <mat-icon class=\"icon\" [ngClass]=\"'file-' + file.fileType\">{{file.icon}}</mat-icon>\n      <div class=\"name\">\n        {{file.file.name}}\n      </div>\n      <span class=\"message\">{{file.message}}</span>\n      <div class=\"status\">\n        <ng-container *ngIf=\"file.status===UploadFileStatus.Uploading\">\n          <mat-progress-spinner\n            *ngIf=\"file.status===UploadFileStatus.Uploading\"\n            mode=\"determinate\"\n            [value]=\"file.percent\"\n            diameter=\"25\"\n            class=\"uploading\"></mat-progress-spinner>\n\n        </ng-container>\n        <mat-progress-spinner\n          *ngIf=\"file.status===UploadFileStatus.Processing\"\n          mode=\"indeterminate\"\n          diameter=\"25\"\n          class=\"processing\"></mat-progress-spinner>\n        <mat-icon *ngIf=\"file.status===UploadFileStatus.Uploaded\" class=\"uploaded\">check_circle</mat-icon>\n        <mat-icon *ngIf=\"file.status===UploadFileStatus.Failed\" class=\"failed\">error</mat-icon>\n        <mat-icon *ngIf=\"file.status===UploadFileStatus.Cancelled\" class=\"cancelled\">report</mat-icon>\n        <a \n            href=\"javascript:;\"\n            *ngIf=\"file.status===UploadFileStatus.Uploading || file.status===UploadFileStatus.Processing\"\n            (click)=\"cancel(file)\"\n            class=\"cancel\"\n            matTooltip=\"Cancel\">\n          <mat-icon>cancel</mat-icon>\n        </a>\n      </div>\n    </div>\n  </div>\n\n</mat-dialog-content>\n", styles: [":host .name{padding-right:10px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;width:100%}:host .close-seconds{font-size:12px}:host .close{margin-right:-7px}:host .message{display:none;color:#d93025;font-size:smaller;padding-right:10px}:host .icon{margin-right:10px;color:#555}:host .container{display:flex;flex-direction:row;align-items:center;margin-bottom:20px}:host .container .status-heading{flex-grow:1;min-width:0;display:flex;flex-direction:row}:host .container .status-heading h2{margin:0 auto}:host .title{flex:1}:host .status{font-size:80%}:host .status .counts span+span:before{content:\", \"}:host .file{display:flex;flex-direction:row;align-items:center;min-height:40px}:host .file mat-icon{color:#8f8f8f}:host .file mat-icon.file-image{color:#d93025}:host .file mat-icon.file-video{color:#7a297b}:host .file .cancel{display:none;color:#d93025}:host .file.failed,:host .file.cancelled{cursor:pointer}:host .file:hover.failed .message,:host .file:hover.cancelled .message{display:flex;flex:1}:host .file:hover.failed .name,:host .file:hover.cancelled .name{display:none}:host .file .uploaded{color:#0f9d58}:host .file .failed{color:#d93025}:host .file .cancelled{color:#e3801d}:host .file.uploading .status:hover .cancel,:host .file.processing .status:hover .cancel{display:inline}:host .file.uploading .status:hover .mat-progress-spinner,:host .file.processing .status:hover .mat-progress-spinner{display:none}@media screen and (max-width: 600px){::ng-deep .fs-upload-pane{margin:0!important;max-width:100vw!important;width:100%!important}::ng-deep .fs-upload-pane .mat-dialog-container{border-radius:0}}\n"], components: [{ type: i3.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }, { type: i4.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { type: i5.MatProgressSpinner, selector: "mat-progress-spinner", inputs: ["color", "mode", "diameter", "strokeWidth", "value"], exportAs: ["matProgressSpinner"] }], directives: [{ type: i1.MatDialogTitle, selector: "[mat-dialog-title], [matDialogTitle]", inputs: ["id"], exportAs: ["matDialogTitle"] }, { type: i6.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1.MatDialogClose, selector: "[mat-dialog-close], [matDialogClose]", inputs: ["type", "mat-dialog-close", "aria-label", "matDialogClose"], exportAs: ["matDialogClose"] }, { type: i1.MatDialogContent, selector: "[mat-dialog-content], mat-dialog-content, [matDialogContent]" }, { type: i6.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i6.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i7.MatTooltip, selector: "[matTooltip]", exportAs: ["matTooltip"] }], pipes: { "fsDateDuration": i8.FsDateDurationPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsUploadComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-component',
                    templateUrl: './upload.component.html',
                    styleUrls: ['./upload.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i1.MatDialogRef }, { type: UploadService }, { type: i0.ChangeDetectorRef }]; } });

class UploadDialog {
    constructor(_dialog, _overlay, _uploadService) {
        this._dialog = _dialog;
        this._overlay = _overlay;
        this._uploadService = _uploadService;
    }
    open() {
        if (this._dialogRef) {
            return;
        }
        this._dialogRef = this._dialog.open(FsUploadComponent, {
            width: '450px',
            hasBackdrop: false,
            panelClass: 'fs-upload-pane',
            position: { bottom: '20px', right: '20px' },
            disableClose: true,
            scrollStrategy: this._overlay.scrollStrategies.noop(),
        });
        const afterClose = this._dialogRef
            .afterClosed()
            .subscribe(result => {
            const files = this._uploadService.files.filter((file) => {
                return file.isCompleted();
            });
            this._uploadService.removeFiles(files);
            this._dialogRef = null;
            afterClose.unsubscribe();
        });
    }
}
UploadDialog.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UploadDialog, deps: [{ token: i1.MatDialog }, { token: i2.Overlay }, { token: UploadService }], target: i0.ɵɵFactoryTarget.Injectable });
UploadDialog.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UploadDialog });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UploadDialog, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.MatDialog }, { type: i2.Overlay }, { type: UploadService }]; } });

class UploadFile {
    constructor(file, cancelSubject) {
        this.statusSubject = new Subject();
        this.percent = 0;
        this.loaded = 0;
        this.message = '';
        this.fileType = 'generic';
        this.icon = 'insert_drive_file';
        this.status = UploadFileStatus.Queued;
        this.started = null;
        this.completed = null;
        this.file = file;
        this.cancelSubject = cancelSubject;
        if (file.type.match(/^image/)) {
            this.icon = 'insert_photo';
            this.fileType = 'image';
        }
        if (file.type.match(/^video/)) {
            this.icon = 'videocam';
            this.fileType = 'video';
        }
    }
    setStatus(status, message) {
        if (status === UploadFileStatus.Uploading && !this.started) {
            this.started = new Date();
        }
        this.status = status;
        this.message = message;
        this.statusSubject.next(status);
        switch (status) {
            case UploadFileStatus.Cancelled:
            case UploadFileStatus.Failed:
            case UploadFileStatus.Uploaded:
                this.completed = new Date();
                this.statusSubject.complete();
                break;
        }
    }
    cancel() {
        this.cancelSubject.next();
        this.setStatus(UploadFileStatus.Cancelled, 'Upload was cancelled');
    }
    get bytesPerSecond() {
        return (this.loaded / this.elapsedSeconds) || 0;
    }
    get elapsedSeconds() {
        const end = this.completed ? this.completed : new Date();
        return (end.getTime() - this.started.getTime()) / 1000;
    }
    update(event) {
        this.loaded = event.loaded;
        this.percent = (event.loaded / this.file.size) * 100;
    }
    isCompleted() {
        return !!this.completed;
    }
}

const FS_UPLOAD_CONFIG = 'fsuploadconfig';
const FS_UPLOAD_CONFIG_ORIGINAL = 'fsuploadconfigoriginal';

const DisplayUploadStatus = new HttpContextToken(() => true);

class UploadInterceptor {
    constructor(_uploadDialog, _uploadService, injector) {
        this._uploadDialog = _uploadDialog;
        this._uploadService = _uploadService;
        this.injector = injector;
        this.config = this.injector.get(FS_UPLOAD_CONFIG)();
    }
    intercept(req, next) {
        let upload = req.body instanceof FormData;
        if (upload) {
            upload = this.config.upload;
            if (req.headers.has('FsUpload')) {
                upload = coerceBooleanProperty(req.headers.get('FsUpload'));
            }
            if (req.context.get(DisplayUploadStatus) === false) {
                upload = false;
            }
        }
        if (!upload) {
            return next.handle(req);
        }
        const cancelPendingRequests = new Subject();
        const r = next.handle(req.clone({
            reportProgress: true,
            headers: req.headers.delete('FsUpload')
        }));
        const files = [];
        for (const entry of req.body.entries()) {
            if (this.isFile(entry[1])) {
                files.push(new UploadFile(entry[1], cancelPendingRequests));
            }
        }
        this._uploadService.addFiles(files);
        this._uploadDialog.open();
        return r.pipe(tap((event) => {
            if (event.type === HttpEventType.Sent) {
                this.setFileStatus(files, UploadFileStatus.Uploading);
            }
            else if (event.type === HttpEventType.UploadProgress) {
                const percent = (event.loaded / event.total) * 100;
                files.forEach((file) => {
                    file.update(event);
                });
                if (percent >= 100) {
                    this.setFileStatus(files, UploadFileStatus.Processing);
                }
                else {
                    this.setFileStatus(files, UploadFileStatus.Uploading);
                }
            }
            else if (event.type === HttpEventType.Response) {
                this.setFileStatus(files, UploadFileStatus.Uploaded);
            }
        }), takeUntil(cancelPendingRequests.asObservable()), catchError((err, caught) => {
            let message = err.message;
            if (err.error && err.error.message) {
                message = err.error.message;
            }
            this.setFileStatus(files, UploadFileStatus.Failed, message);
            return throwError(err);
        }), finalize(() => {
            const failed = files.some((file) => file.status === UploadFileStatus.Failed);
            if (!failed) {
                this.setFileStatus(files, UploadFileStatus.Uploaded);
            }
        }));
    }
    setFileStatus(files, status, message) {
        files.forEach((file) => {
            file.setStatus(status, message);
        });
    }
    isFile(file) {
        return file instanceof File ||
            (typeof file === 'object' && file.constructor && file.constructor.name === 'File') ||
            (typeof file === 'object' && typeof file.name === 'string' && file instanceof Blob);
    }
}
UploadInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UploadInterceptor, deps: [{ token: UploadDialog }, { token: UploadService }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
UploadInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UploadInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UploadInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: UploadDialog }, { type: UploadService }, { type: i0.Injector }]; } });

function FsUploadConfigInit(config) {
    return (() => {
        config = config || {};
        if (config.upload === undefined) {
            config.upload = true;
        }
        return config;
    });
}

class FsUploadModule {
    static forRoot(config) {
        return {
            ngModule: FsUploadModule,
            providers: [
                UploadDialog,
                UploadService,
                { provide: HTTP_INTERCEPTORS, useClass: UploadInterceptor,
                    deps: [UploadDialog, UploadService, Injector], multi: true },
                { provide: FS_UPLOAD_CONFIG_ORIGINAL, useValue: config },
                { provide: FS_UPLOAD_CONFIG, useFactory: FsUploadConfigInit, deps: [FS_UPLOAD_CONFIG_ORIGINAL] },
            ]
        };
    }
}
FsUploadModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsUploadModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsUploadModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsUploadModule, declarations: [FsUploadComponent], imports: [CommonModule,
        MatDialogModule,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        FsDateModule], exports: [FsUploadComponent] });
FsUploadModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsUploadModule, imports: [[
            CommonModule,
            MatDialogModule,
            MatIconModule,
            MatButtonModule,
            MatTooltipModule,
            MatProgressSpinnerModule,
            FsDateModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsUploadModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        MatDialogModule,
                        MatIconModule,
                        MatButtonModule,
                        MatTooltipModule,
                        MatProgressSpinnerModule,
                        FsDateModule,
                    ],
                    exports: [
                        FsUploadComponent
                    ],
                    entryComponents: [
                        FsUploadComponent
                    ],
                    declarations: [
                        FsUploadComponent,
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DisplayUploadStatus, FsUploadComponent, FsUploadModule, UploadDialog };
